/**
 * «Agrega tu KPI» del repositorio de Redes Sociales: fórmulas personales sobre las métricas
 * (p. ej. `interacciones_mias = clics + saves`). Se guardan solo en el navegador del usuario.
 *
 * Evaluación: cada campo de la fórmula se suma sobre las filas del contexto (un post, un
 * perfil en el periodo, un mes…) y la fórmula se aplica a esos totales. Así una suma da lo
 * mismo que fila por fila y un cociente (interacciones / alcance) sale bien ponderado.
 */
import { POST_METRICS, num } from './socialRepoUtils';

/** Campos que puede usar una fórmula y en qué datos existen. */
export const KPI_FIELDS = [
    ...POST_METRICS.map(name => ({ name, group: 'posts' })),
    { name: 'Publicaciones', group: 'posts' },
    { name: 'Nuevos', group: 'followers' },
    { name: 'Perdidos', group: 'followers' },
    { name: 'Neto', group: 'followers' },
];

/* Sinónimos aceptados al escribir (sin acentos, minúsculas, sin espacios). */
const ALIASES = {
    saves: 'Guardados', guardados: 'Guardados', saved: 'Guardados',
    clicks: 'Clics', clics: 'Clics', clic: 'Clics',
    likes: 'Likes', megusta: 'Likes',
    shares: 'Compartidos', compartidos: 'Compartidos',
    comments: 'Comentarios', comentarios: 'Comentarios',
    reach: 'Alcance', alcance: 'Alcance',
    impressions: 'Impresiones', impresiones: 'Impresiones',
    views: 'Vistas', vistas: 'Vistas',
    reactions: 'Reacciones', reacciones: 'Reacciones',
    interactions: 'Interacciones', interacciones: 'Interacciones', engagement: 'Interacciones',
    follows: 'Seguimientos', seguimientos: 'Seguimientos',
    posts: 'Publicaciones', publicaciones: 'Publicaciones',
    new: 'Nuevos', nuevos: 'Nuevos', lost: 'Perdidos', perdidos: 'Perdidos', net: 'Neto', neto: 'Neto',
};

export const KPI_FORMATS = [
    { id: 'number', label: 'Número' },
    { id: 'decimal', label: 'Decimal (2)' },
    { id: 'percent', label: 'Porcentaje' },
];

const normalize = (text) => String(text || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\(s\)/g, 's')
    .replace(/[^a-z0-9]+/g, '');

const FIELD_INDEX = new Map(KPI_FIELDS.map(field => [normalize(field.name), field.name]));

/** Nombre canónico de un campo escrito por el usuario, o null. */
export function resolveField(raw) {
    const key = normalize(raw);
    return FIELD_INDEX.get(key) || ALIASES[key] || null;
}

/** Nombre de KPI válido: letras, números y guion bajo; no puede chocar con un campo. */
export function validateKpiName(name, existing = []) {
    const clean = String(name || '').trim();
    if (!clean) return 'Ponle un nombre.';
    if (clean.length > 40) return 'Máximo 40 caracteres.';
    if (!/^[\p{L}\p{N}_ ]+$/u.test(clean)) return 'Usa solo letras, números, espacios o guion bajo.';
    if (resolveField(clean)) return 'Ese nombre ya es una métrica del repositorio.';
    if (existing.some(item => item.toLowerCase() === clean.toLowerCase())) return 'Ya tienes un KPI con ese nombre.';
    return '';
}

function tokenize(text) {
    const tokens = [];
    let i = 0;
    const src = String(text || '');
    while (i < src.length) {
        const ch = src[i];
        if (/\s/.test(ch)) { i += 1; continue; }
        if ('+-*/()'.includes(ch)) { tokens.push({ type: 'op', value: ch, pos: i }); i += 1; continue; }
        if (ch === '[') {
            const close = src.indexOf(']', i);
            if (close === -1) throw new Error('Falta cerrar un corchete «]».');
            tokens.push({ type: 'field', value: src.slice(i + 1, close), pos: i });
            i = close + 1;
            continue;
        }
        const numMatch = src.slice(i).match(/^\d+(?:[.,]\d+)?/);
        if (numMatch) {
            tokens.push({ type: 'num', value: Number(numMatch[0].replace(',', '.')), pos: i });
            i += numMatch[0].length;
            continue;
        }
        const idMatch = src.slice(i).match(/^[\p{L}_][\p{L}\p{N}_]*/u);
        if (idMatch) {
            tokens.push({ type: 'field', value: idMatch[0], pos: i });
            i += idMatch[0].length;
            continue;
        }
        throw new Error(`Carácter no válido «${ch}».`);
    }
    return tokens;
}

/**
 * Compila una fórmula a un árbol. Gramática: suma/resta de términos, términos de
 * multiplicación/división, factores (número, campo, paréntesis, signo).
 * @returns {{ast: object, fields: string[]}}
 */
export function compileFormula(text) {
    const tokens = tokenize(text);
    if (!tokens.length) throw new Error('Escribe una fórmula.');
    let pos = 0;
    const fields = new Set();
    const peek = () => tokens[pos];
    const take = () => tokens[pos++];

    function factor() {
        const tok = take();
        if (!tok) throw new Error('La fórmula está incompleta.');
        if (tok.type === 'num') return { type: 'num', value: tok.value };
        if (tok.type === 'field') {
            const name = resolveField(tok.value);
            if (!name) throw new Error(`No conozco el campo «${tok.value}».`);
            fields.add(name);
            return { type: 'field', name };
        }
        if (tok.value === '(') {
            const inner = expr();
            const close = take();
            if (!close || close.value !== ')') throw new Error('Falta cerrar un paréntesis «)».');
            return inner;
        }
        if (tok.value === '-') return { type: 'neg', arg: factor() };
        if (tok.value === '+') return factor();
        throw new Error(`Sobra «${tok.value}».`);
    }
    function term() {
        let node = factor();
        while (peek() && (peek().value === '*' || peek().value === '/')) {
            const op = take().value;
            node = { type: 'bin', op, left: node, right: factor() };
        }
        return node;
    }
    function expr() {
        let node = term();
        while (peek() && (peek().value === '+' || peek().value === '-')) {
            const op = take().value;
            node = { type: 'bin', op, left: node, right: term() };
        }
        return node;
    }
    const ast = expr();
    if (pos < tokens.length) throw new Error(`Sobra «${tokens[pos].value}».`);
    if (!fields.size) throw new Error('Usa al menos una métrica.');
    return { ast, fields: Array.from(fields) };
}

function evaluate(node, values) {
    switch (node.type) {
        case 'num': return node.value;
        case 'field': return values[node.name] ?? 0;
        case 'neg': {
            const v = evaluate(node.arg, values);
            return v == null ? null : -v;
        }
        default: {
            const a = evaluate(node.left, values);
            const b = evaluate(node.right, values);
            if (a == null || b == null) return null;
            if (node.op === '+') return a + b;
            if (node.op === '-') return a - b;
            if (node.op === '*') return a * b;
            return b === 0 ? null : a / b;
        }
    }
}

/** KPI listo para usar: compila y guarda los campos. Lanza si la fórmula no es válida. */
export function prepareKpi(kpi) {
    const { ast, fields } = compileFormula(kpi.formula);
    const groups = new Set(fields.map(name => KPI_FIELDS.find(f => f.name === name)?.group));
    return { ...kpi, ast, fields, groups: Array.from(groups) };
}

/** ¿El KPI se puede calcular con estos campos disponibles? */
export const kpiAppliesTo = (kpi, availableFields) => kpi.fields.every(field => availableFields.includes(field));

/** Valor del KPI sobre un conjunto de filas (suma cada campo y aplica la fórmula). */
export function evaluateKpi(kpi, rows) {
    const list = Array.isArray(rows) ? rows : [rows];
    if (!list.length) return null;
    const totals = {};
    kpi.fields.forEach((field) => {
        totals[field] = list.reduce((acc, row) => acc + num(row?.[field]), 0);
    });
    const value = evaluate(kpi.ast, totals);
    return Number.isFinite(value) ? value : null;
}

const numberFmt = new Intl.NumberFormat('es-MX', { maximumFractionDigits: 0 });
const decimalFmt = new Intl.NumberFormat('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Valor formateado según el formato elegido para el KPI. */
export function formatKpi(kpi, value) {
    if (value == null) return '—';
    if (kpi.format === 'percent') return `${(value * 100).toFixed(2)}%`;
    if (kpi.format === 'decimal') return decimalFmt.format(value);
    return numberFmt.format(value);
}

const STORAGE_PREFIX = 'cm-repo-kpis:';

/** KPIs guardados del usuario en este navegador (lista vacía si no hay o falla el almacenamiento). */
export function loadKpis(repo, uid) {
    try {
        const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${repo}:${uid || 'anon'}`);
        const list = JSON.parse(raw || '[]');
        return Array.isArray(list) ? list.filter(item => item && item.id && item.name && item.formula) : [];
    } catch (error) {
        return [];
    }
}

/** Guarda los KPIs del usuario; si el navegador no deja, quedan solo en esta sesión. */
export function saveKpis(repo, uid, list) {
    try {
        window.localStorage.setItem(`${STORAGE_PREFIX}${repo}:${uid || 'anon'}`, JSON.stringify(
            list.map(({ id, name, formula, format }) => ({ id, name, formula, format }))
        ));
    } catch (error) {
        /* Navegación privada o almacenamiento bloqueado: se conserva en memoria. */
    }
}
